import { chain, path, pathOr } from 'ramda'

import { OPERATIONS, ORDER_LINE_TYPES, SUBSCRIPTION_TYPES } from './constants'

export const normalizeKlarnaOrder = ({
  order_id,
  merchant_reference1,
  created_at,
  order_amount,
  order_lines = [],
  captures = [],
  refunds = [],
  payment_method = {},
}: any): any => {
  const orderLines: any = pathOr(order_lines, [0, 'order_lines'], captures)

  const sortByUnitPrice = (a, b): any => b.unit_price - a.unit_price

  const returnedProducts = chain((refund: any) => refund.order_lines, refunds).sort(sortByUnitPrice)

  const products = orderLines.filter(
    (orderLine) =>
      ![
        ORDER_LINE_TYPES.DISCOUNT,
        ORDER_LINE_TYPES.GIFT_CARD,
        ORDER_LINE_TYPES.SHIPPING_FEE,
        ORDER_LINE_TYPES.SALES_TAX,
      ].includes(orderLine.type),
  )

  const notReturnedProducts = products
    .reduce((productList, product) => {
      const matchingReturn: any = returnedProducts.find(
        (returnedProduct: any) => returnedProduct.reference === product.reference,
      )
      // Filter out fully returned products, or decrease the quantity for partially returned products
      return matchingReturn
        ? [
            ...productList,
            ...(matchingReturn.quantity < product.quantity
              ? [
                  {
                    ...product,
                    quantity: product.quantity - matchingReturn.quantity,
                    total_amount: product.total_amount - matchingReturn.total_amount,
                  },
                ]
              : []),
          ]
        : [...productList, product]
    }, [])
    .sort(sortByUnitPrice)

  const paymentDetails: any = pathOr({}, [0, 'payment_details'], captures)

  return {
    orderId: order_id,
    hmOrderNumber: merchant_reference1,
    createdAt: created_at,
    products: notReturnedProducts,
    returnedProducts,
    discounts: orderLines.filter((orderLine) => orderLine.type === ORDER_LINE_TYPES.DISCOUNT),
    giftCards: orderLines.filter((orderLine) => orderLine.type === ORDER_LINE_TYPES.GIFT_CARD),
    shippingFees: orderLines.filter(
      (orderLine) => orderLine.type === ORDER_LINE_TYPES.SHIPPING_FEE,
    ),
    salesTaxes: orderLines.filter((orderLine) => orderLine.type === ORDER_LINE_TYPES.SALES_TAX),
    returnedAmount: -refunds.reduce((sum, refund) => sum + (refund.refunded_amount || 0), 0),
    servicingCharges: paymentDetails.servicing_charges || [],
    totalAmount: paymentDetails.total_amount >= 0 ? paymentDetails.total_amount : order_amount,
    amountPaid: paymentDetails.amount_paid,
    amountLeftToPay: paymentDetails.amount_left_to_pay,
    status: paymentDetails.status,
    statusDescription: paymentDetails.status_description,
    paymentMethod:
      path(['payment_method', 'description'], paymentDetails) || payment_method.description,
    dueDate: paymentDetails.due_date,
    operations: paymentDetails.operations || [],
  }
}

export const normalizeKlarnaOrders = (klarnaResponse): any => ({
  orders: pathOr([], ['orders'], klarnaResponse).map(normalizeKlarnaOrder),
  totalNumberOfOrders: path(['pagination', 'total'], klarnaResponse),
  nbrOrdersInResponse: path(['pagination', 'count'], klarnaResponse),
})

export const normalizeKlarnaProfile = (klarnaResponse): any => ({
  operations: {
    manageMonthly: pathOr([], ['operations'], klarnaResponse).find(
      (operation: any) => operation.type === OPERATIONS.MANAGE_MONTHLY,
    ),
    manageCard: pathOr([], ['operations'], klarnaResponse).find(
      (operation: any) => operation.type === OPERATIONS.MANAGE_CARD,
    ),
  },
  subscription: pathOr([], ['subscriptions'], klarnaResponse).find(
    (subscription: any) => subscription.type === SUBSCRIPTION_TYPES.MERCHANT_MONTHLY,
  ),
})

export const normalizeKlarnaMonthlyInvoice = (klarnaResponse): any => ({
  id: klarnaResponse.collection_id,
  ...klarnaResponse,
})

export const normalizeKlarnaMonthlyInvoices = (klarnaResponse): any => ({
  monthlyInvoices: klarnaResponse.collections.map(normalizeKlarnaMonthlyInvoice),
  totalNumberOfInvoices: path(['pagination', 'total'], klarnaResponse),
  nbrInvoicesInResponse: path(['pagination', 'count'], klarnaResponse),
})
